// A connection dragged on the plan (docs/claude/connections.md *Drags and
// handles*): an end re-glued where a pick puts it, a bend moved, its tag slid
// along it — each drag move one edit (connEdits.ts). The ONE place a bend dragged
// on the plan lands, whichever press moves it (its grip, the double-click that
// adds and picks it up): the plan's own snaps first (an object snap wins; else
// alignment with the other points, 45° / square from the point before, the
// grid), then a pull to square with the points either side — a pipe's at 45°
// too, keeping its level; a hose never snaps, nor does a drag with Alt. Pure (no
// DOM): the app finds the snaps under the pointer and makes the undo step.

import type {
  ConnEnd,
  Vec2,
} from '../shared/types';
import type { ConnGeom } from './connections';
import type { ConnEdit } from './connEdits';
import { nearestFractionOnPath } from './geometry';
import { snapPipeVertex } from './pipes';

/** What the plan's own snaps made of the pointer: where they put it and whether
 *  an object snap (OSNAP) caught it. */
export interface PlanSnap { p: Vec2; osnap: boolean }

/** A bend being dragged on the plan. */
export interface BendDrag {
  /** The connection resolved (`connGeom`). */
  g: ConnGeom;
  /** The bend: its index in `waypoints`. */
  i: number;
  /** The pointer (world mm). */
  raw: Vec2;
  /** The plan's own snaps at the pointer; null with Alt (free). */
  snap: PlanSnap | null;
  /** How near the square (or a pipe's 45°) line through a neighbour pulls it (mm). */
  tol: number;
}

/** Snap a dragged routing vertex so its two adjoining segments run horizontal /
 *  vertical where they can: X to the nearer neighbour's column, Y to the nearer
 *  neighbour's row, each within `tol` of `raw`; an axis that lines up with
 *  neither keeps `base`. */
export function squareToNeighbours(raw: Vec2, base: Vec2, prev: Vec2, next: Vec2, tol: number): Vec2 {
  let x = base.x, bx = tol;
  for (const cx of [prev.x, next.x]) { const d = Math.abs(raw.x - cx); if (d < bx) { bx = d; x = cx; } }
  let y = base.y, by = tol;
  for (const cy of [prev.y, next.y]) { const d = Math.abs(raw.y - cy); if (d < by) { by = d; y = cy; } }
  return { x, y };
}

/** Where a bend dragged on the plan lands (the module's rule): free with Alt or
 *  on a hose; on an object snap when one caught it; else where the plan's snaps
 *  put it, pulled square to the points either side — a rigid pipe's square or at
 *  45° (`snapPipeVertex` on its level, its fall carried). */
export function bendPlanPoint(b: BendDrag): Vec2 {
  const { g, i } = b;
  if (!b.snap || g.pipe?.flexible) return { x: b.raw.x, y: b.raw.y };
  if (b.snap.osnap) return b.snap.p;
  const prev = g.ctrl[i], next = g.ctrl[i + 2];
  if (!g.pipe) return squareToNeighbours(b.raw, b.snap.p, prev, next, b.tol);
  const z = g.ctrl[i + 1].z;
  const q = snapPipeVertex({ ...b.snap.p, z }, prev, next, g.pipe.slope, { kind: 'plane', z }, b.tol).p;
  return { x: q.x, y: q.y };
}

/** A bend dragged on the plan, as its edit: moved where `bendPlanPoint` puts it,
 *  its height kept (it moves on its level). */
export function bendDragEdit(b: BendDrag): ConnEdit {
  return { kind: 'bendMove', i: b.i, p: bendPlanPoint(b) };
}

/** A free bend clicked while a connection is drawn (the Connect tool): a rigid
 *  pipe's snaps square or at 45° in plan to the point before it; a line's and a
 *  hose's stand where clicked. */
export function draftBendPoint(prev: Vec2, p: Vec2, pipe: { flexible: boolean } | null, tol: number): Vec2 {
  if (!pipe || pipe.flexible) return { x: p.x, y: p.y };
  const q = snapPipeVertex({ ...p, z: 0 }, { ...prev, z: 0 }, null, 0, { kind: 'plane', z: 0 }, tol).p;
  return { x: q.x, y: q.y };
}

/** An end dragged to where a pick put it (the Connect tool's own pick: another
 *  object, one of its points, or a free point), as its edit — its height kept. */
export function endDragEdit(which: 'from' | 'to', pick: { id: string; end?: ConnEnd }): ConnEdit {
  return { kind: 'reglue', which, id: pick.id, end: pick.end };
}

/** The tag dragged to `world`: held on the plan route, at the point nearest it. */
export function tagDragEdit(g: ConnGeom, world: Vec2): ConnEdit {
  return { kind: 'tag', t: nearestFractionOnPath(g.plan.pts, world) };
}

/** The plan-route segment under `world` within `tol` (mm), first along the
 *  route: the leg it belongs to (where a bend added there goes in `waypoints`)
 *  and the point on it. */
export function planRouteHit(g: ConnGeom, world: Vec2, tol: number): { leg: number; p: Vec2 } | null {
  const pts = g.plan.pts;
  for (let s = 0; s + 1 < pts.length; s++) {
    const a = pts[s], b = pts[s + 1];
    const dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((world.x - a.x) * dx + (world.y - a.y) * dy) / L2)) : 0;
    const p = { x: a.x + t * dx, y: a.y + t * dy };
    if (Math.hypot(world.x - p.x, world.y - p.y) <= tol) return { leg: g.plan.leg[s], p };
  }
  return null;
}
